import { setUtmAttribution, getUtmAttribution, type UtmParams } from './analyticsSession'

/**
 * Захват рекламных меток из адреса страницы.
 *
 * Ловить нужно на ПЕРВОМ заходе: после навигации по сайту `utm_*` из URL исчезают,
 * и связь «реклама → заказ» теряется навсегда. Поэтому метки сразу переезжают
 * в атрибуцию, которая живёт 30 суток и прилипает ко всем событиям.
 *
 * Считаем по последнему источнику (last-click): если человек пришёл сначала из ВК,
 * а потом из Яндекса, заказ засчитывается Яндексу — так считают рекламные кабинеты,
 * с которыми эти цифры будут сверять.
 */

/** Пары «параметр в URL» → «поле атрибуции». Порядок задаёт и порядок в отчёте. */
const UTM_FIELDS: ReadonlyArray<[string, keyof UtmParams]> = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_content', 'utmContent'],
  ['utm_term', 'utmTerm'],
]

/** Значения режем: метки приходят из внешних систем, а колонки в БД ограничены. */
const MAX_VALUE_LENGTH = 128

const readUtmFromSearch = (search: string): UtmParams => {
  const params = new URLSearchParams(search)
  const utm: UtmParams = {}

  for (const [param, field] of UTM_FIELDS) {
    const value = params.get(param)?.trim()
    if (value) utm[field] = value.slice(0, MAX_VALUE_LENGTH)
  }

  return utm
}

/**
 * Разбирает метки и, если они есть, фиксирует источник входа.
 *
 * @returns true, если метки были и атрибуция обновлена.
 */
export const captureUtmFromUrl = (search: string = window.location.search): boolean => {
  if (!process.client) return false

  const utm = readUtmFromSearch(search)

  // Кампанию без источника не считаем переходом по рекламе: одинокий utm_campaign
  // прилетает и от вставок в мессенджерах, где источник теряется.
  if (!utm.utmSource) return false

  // Свой слот, а не общая атрибуция входа: пуш и deeplink не должны затирать рекламу
  // внутри её 30-суточного окна.
  setUtmAttribution(utm)

  return true
}

/** Метки текущей атрибуции — их подмешивают события и создание заказа. */
export const getUtmParams = (): UtmParams => {
  const utm = getUtmAttribution()
  if (!utm) return {}

  return {
    utmSource: utm.utmSource,
    utmMedium: utm.utmMedium,
    utmCampaign: utm.utmCampaign,
    utmContent: utm.utmContent,
    utmTerm: utm.utmTerm,
  }
}
